import { przezmorzeApi } from "../../../api/api";
import type {
  StatisticsSummary,
  OrderRegionBreakdownEntry,
  ActiveCustomerRow,
  InactiveCustomerRow,
  SellerOrdersStatsResponse,
  AcquiredCustomerRow,
  SellerActivityStatsResponse,
  CustomerStatisticsResponse,
  OrdersTrendGranularity,
  OrdersTrendBucket,
  CustomerOrdersTrendResponse,
  ProviderProductRow,
  ProviderStatsResponse,
} from "@przezmorze/api-types";

export type {
  StatisticsSummary,
  OrderRegionBreakdownEntry,
  ActiveCustomerRow,
  InactiveCustomerRow,
  SellerOrdersStatsResponse,
  AcquiredCustomerRow,
  SellerActivityStatsResponse,
  CustomerStatisticsResponse,
  OrdersTrendGranularity,
  OrdersTrendBucket,
  CustomerOrdersTrendResponse,
  ProviderProductRow,
  ProviderStatsResponse,
};

export interface SellerIdsFilter {
  readonly sellerIds: string[] | "all";
}

function sellerIdsParam(sellerIds: string[] | "all"): string {
  return sellerIds === "all" ? "all" : sellerIds.join(",");
}

export interface SellerOrdersStatisticsParams extends SellerIdsFilter {
  readonly from: string;
  readonly to?: string;
}

export async function getSellerOrdersStatistics(
  params: SellerOrdersStatisticsParams,
  signal?: AbortSignal,
): Promise<SellerOrdersStatsResponse> {
  const response = await przezmorzeApi.get<SellerOrdersStatsResponse>("/statistics/sellers/orders", {
    params: { sellerIds: sellerIdsParam(params.sellerIds), from: params.from, to: params.to },
    signal,
  });
  return response.data;
}

export interface SellerActivityStatisticsParams extends SellerIdsFilter {
  readonly from: string;
  readonly to: string;
}

export async function getSellerActivityStatistics(
  params: SellerActivityStatisticsParams,
  signal?: AbortSignal,
): Promise<SellerActivityStatsResponse> {
  const response = await przezmorzeApi.get<SellerActivityStatsResponse>("/statistics/sellers/activity", {
    params: { sellerIds: sellerIdsParam(params.sellerIds), from: params.from, to: params.to },
    signal,
  });
  return response.data;
}

export interface CustomerStatisticsParams {
  readonly from: string;
  readonly to?: string;
  readonly includeMargin?: boolean;
}

export async function getCustomerStatistics(
  customerId: string,
  params: CustomerStatisticsParams,
  signal?: AbortSignal,
): Promise<CustomerStatisticsResponse> {
  const response = await przezmorzeApi.get<CustomerStatisticsResponse>(`/statistics/customers/${customerId}`, {
    params,
    signal,
  });
  return response.data;
}

export interface CustomerOrdersTrendParams {
  readonly from: string;
  readonly to?: string;
  readonly granularity: OrdersTrendGranularity;
}

export async function getCustomerOrdersTrend(
  customerId: string,
  params: CustomerOrdersTrendParams,
  signal?: AbortSignal,
): Promise<CustomerOrdersTrendResponse> {
  const response = await przezmorzeApi.get<CustomerOrdersTrendResponse>(
    `/statistics/customers/${customerId}/orders-trend`,
    { params, signal },
  );
  return response.data;
}

export interface ProviderStatisticsParams {
  readonly from: string;
  readonly to?: string;
}

export async function getProviderStatistics(
  providerId: string,
  params: ProviderStatisticsParams,
  signal?: AbortSignal,
): Promise<ProviderStatsResponse> {
  const response = await przezmorzeApi.get<ProviderStatsResponse>(`/statistics/providers/${providerId}`, {
    params,
    signal,
  });
  return response.data;
}
